import {
  forwardRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode
} from 'react';

/* ────────────────────────────────────── RecordCardList */

export interface RecordCardListProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Vertical stack layout for responsive table→card fallbacks and other
 * full-width record lists. Page CSS may still control grid vs column; this
 * primitive owns inter-card rhythm only.
 */
export function RecordCardList({
  children,
  className,
  ...rest
}: RecordCardListProps) {
  return (
    <div
      className={['pn-record-card-list', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

/* ────────────────────────────────────── RecordCard */

export type RecordCardLayout = 'stack' | 'row';

export interface RecordCardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** stack = vertical list cards; row = board-style with optional lead rail */
  layout?: RecordCardLayout;
  /** Adds button semantics + keyboard activation when `onActivate` is set */
  interactive?: boolean;
  onActivate?: () => void;
  selected?: boolean;
  dragging?: boolean;
}

function activateOnKey(
  event: KeyboardEvent<HTMLDivElement>,
  onActivate?: () => void
): void {
  if (!onActivate) return;
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  onActivate();
}

const RecordCardRoot = forwardRef<HTMLDivElement, RecordCardProps>(
  function RecordCard(
    {
      children,
      className,
      layout = 'stack',
      interactive = false,
      onActivate,
      selected = false,
      dragging = false,
      onClick,
      onKeyDown,
      ...rest
    },
    ref
  ) {
    const cls = [
      'pn-record-card',
      `pn-record-card--${layout}`,
      interactive ? 'pn-record-card--interactive' : null,
      selected ? 'pn-record-card--selected' : null,
      dragging ? 'pn-record-card--dragging' : null,
      className
    ]
      .filter(Boolean)
      .join(' ');

    function handleClick(event: MouseEvent<HTMLDivElement>) {
      onClick?.(event);
      if (event.defaultPrevented) return;
      if (interactive && onActivate) onActivate();
    }

    function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;
      activateOnKey(event, interactive ? onActivate : undefined);
    }

    return (
      <div
        ref={ref}
        className={cls}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        data-selected={selected ? '1' : '0'}
        data-dragging={dragging ? 'true' : undefined}
        onClick={interactive ? handleClick : onClick}
        onKeyDown={interactive ? handleKeyDown : onKeyDown}
        {...rest}
      >
        {children}
      </div>
    );
  }
);

/* ────────────────────────────────────── Subcomponents */

function RecordCardLead({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={['pn-record-card-lead', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

function RecordCardBody({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={['pn-record-card-body', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

function RecordCardTitle({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={['pn-record-card-title', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

function RecordCardMeta({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={['pn-record-card-meta', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface RecordCardChipProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
  /** When set, renders as a link-styled chip */
  href?: string;
  onChipClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

function RecordCardChip({
  children,
  className,
  href,
  onChipClick,
  onClick,
  ...rest
}: RecordCardChipProps) {
  const cls = ['pn-record-card-chip', href ? 'pn-record-card-chip--link' : null, className]
    .filter(Boolean)
    .join(' ');

  if (href) {
    return (
      <a
        href={href}
        className={cls}
        onClick={(event) => {
          onChipClick?.(event);
          if (!event.defaultPrevented) onClick?.(event as unknown as React.MouseEvent<HTMLSpanElement>);
        }}
        {...(rest as HTMLAttributes<HTMLAnchorElement>)}
      >
        {children}
      </a>
    );
  }

  return (
    <span className={cls} onClick={onClick} {...rest}>
      {children}
    </span>
  );
}

function RecordCardTop({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={['pn-record-card-top', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

function RecordCardFoot({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={['pn-record-card-foot', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

function RecordCardActions({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={['pn-record-card-actions', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface RecordCardDragRailProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  dragging?: boolean;
  preview?: boolean;
}

function RecordCardDragRail({
  children,
  className,
  dragging = false,
  preview = false,
  ...rest
}: RecordCardDragRailProps) {
  return (
    <button
      type="button"
      className={[
        'pn-record-card-drag-rail',
        preview ? 'pn-record-card-drag-rail--preview' : null,
        className
      ]
        .filter(Boolean)
        .join(' ')}
      data-dragging={dragging ? 'true' : undefined}
      {...rest}
    >
      {children ?? (
        <span className="pn-record-card-drag-grip" aria-hidden>
          ⠿
        </span>
      )}
    </button>
  );
}

export const RecordCard = Object.assign(RecordCardRoot, {
  Lead: RecordCardLead,
  Body: RecordCardBody,
  Title: RecordCardTitle,
  Meta: RecordCardMeta,
  Chip: RecordCardChip,
  Top: RecordCardTop,
  Foot: RecordCardFoot,
  Actions: RecordCardActions,
  DragRail: RecordCardDragRail
});
